import type { ExamQuestion, LearningPathLesson } from '../types';
import { coursePlanLessons, levelTargets } from './coursePlan';

const choice = (id: string, prompt: string, options: string[], answer: string, explanation: string, topic: string): ExamQuestion => ({
  id,
  type: 'choice',
  prompt,
  options,
  answer,
  explanation,
  topic,
});

const fill = (id: string, prompt: string, answer: string, explanation: string, topic: string): ExamQuestion => ({
  id,
  type: 'complete',
  prompt,
  options: [],
  answer,
  explanation,
  topic,
});

const examFor = (lesson: (typeof coursePlanLessons)[number]): ExamQuestion[] => {
  const firstWord = lesson.category;
  const finalWord = lesson.example.en.replace(/[.!?]/g, '').split(' ').at(-1) ?? '';
  return [
    choice(`e-${lesson.id}-1`, `What is the topic of "${lesson.title}"?`, [lesson.category, 'random grammar', 'unrelated sport'], lesson.category, `The lesson focuses on ${lesson.topic}.`, lesson.topic),
    choice(`e-${lesson.id}-2`, 'Choose the correct lesson sentence.', [lesson.example.en, 'I is ready.', 'She are go.'], lesson.example.en, `This sentence uses ${lesson.grammarFocus}.`, lesson.topic),
    choice(`e-${lesson.id}-3`, 'Which grammar focus belongs to this lesson?', [lesson.grammarFocus, 'future perfect continuous only', 'Latin pronunciation'], lesson.grammarFocus, 'The grammar focus is shown at the top of the lesson.', lesson.topic),
    fill(`e-${lesson.id}-4`, `Complete the final word: ${lesson.example.en.split(' ').slice(0, -1).join(' ')} ____`, finalWord, 'Complete the model sentence from the lesson.', lesson.topic),
    choice(`e-${lesson.id}-5`, lesson.level === 'Beginner' || lesson.level === 'A1' ? 'What should you check first?' : 'What makes a strong answer?', lesson.level === 'Beginner' || lesson.level === 'A1' ? ['Russian meaning, then English practice', 'Only fast speech', 'No examples'] : ['Clear point, support and example', 'One memorised word', 'No structure'], lesson.level === 'Beginner' || lesson.level === 'A1' ? 'Russian meaning, then English practice' : 'Clear point, support and example', lesson.ruSupport, lesson.topic),
    choice(`e-${lesson.id}-6`, `Which word or phrase is useful here?`, [firstWord, 'delete progress', 'wrong answer only'], firstWord, `This vocabulary belongs to ${lesson.category}.`, lesson.topic),
    choice(`e-${lesson.id}-7`, 'What is the best speaking action?', ['Answer out loud with a complete idea', 'Read silently only', 'Skip pronunciation'], 'Answer out loud with a complete idea', 'Speaking improves when you produce English aloud.', lesson.topic),
    choice(`e-${lesson.id}-8`, 'What score unlocks the next lesson?', ['70%', '20%', '100% only'], '70%', 'The app uses a 70% pass mark.', lesson.topic),
  ];
};

export const learningPathLessons: LearningPathLesson[] = coursePlanLessons.map((lesson) => ({
  id: lesson.id,
  level: lesson.level,
  lessonNumber: lesson.lessonNumber,
  title: lesson.title,
  topic: lesson.topic,
  warmup: [
    lesson.level === 'Beginner' || lesson.level === 'A1' ? `По-русски: ${lesson.ruSupport}` : `Focus your answer on ${lesson.category}.`,
    `Say the model sentence: ${lesson.example.en}`,
    `Notice the grammar focus: ${lesson.grammarFocus}.`,
  ],
  vocabularyIds: Array.from({ length: 8 }, (_, index) => `v-${lesson.id}-${index + 1}`),
  phraseIds: Array.from({ length: 4 }, (_, index) => `p-${lesson.id}-${index + 1}`),
  grammarId: `g-${lesson.id}`,
  listeningIds: [`l-${lesson.id}`],
  speakingIds: [`s-${lesson.id}`],
  readingId: `r-${lesson.id}`,
  writingId: `w-${lesson.id}`,
  examples: [
    lesson.example,
    { en: lesson.level === 'B2' || lesson.level === 'C1' ? `This issue requires evidence and a balanced conclusion.` : `I can practise ${lesson.category} today.`, ru: lesson.level === 'B2' || lesson.level === 'C1' ? 'Эта проблема требует доказательств и сбалансированного вывода.' : `Я могу практиковать тему "${lesson.category}" сегодня.`, pattern: 'supporting sentence' },
    { en: lesson.level === 'Beginner' || lesson.level === 'A1' ? 'Please repeat the sentence slowly.' : `A strong answer connects ${lesson.category} with a clear example.`, ru: lesson.level === 'Beginner' || lesson.level === 'A1' ? 'Пожалуйста, повторите предложение медленно.' : `Сильный ответ связывает тему "${lesson.category}" с ясным примером.`, pattern: 'practice extension' },
  ],
  exam: examFor(lesson),
}));

export { levelTargets };
